import { useEffect, useState } from "react";
import Header from "./Header";
import { useToast } from "@chakra-ui/react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
} from "@chakra-ui/react";
import { PhoneIcon, EmailIcon } from "@chakra-ui/icons";

const LEAD_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "category", label: "Category" },
  { key: "store_name", label: "Store" },
  { key: "type", label: "Type" },
  { key: "production_name", label: "Project Name" },
  { key: "hirer_name", label: "Hirer (Client)" },
  { key: "authorized_contact", label: "Authorized Contact" },
  { key: "project_type", label: "Project Type" },
  { key: "reporting_datetime", label: "Reporting Date & Time" },
  { key: "completion_date", label: "Completion Date" },
  { key: "total_units", label: "Total Units/Shifts" },
  { key: "location_address", label: "Location Address" },
  { key: "status", label: "Status" },
  { key: "createdAt", label: "Date" },
];

function Leads() {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const login = JSON.parse(localStorage.getItem("login")) || "";
  const toast = useToast();
  const [plan, setPlan] = useState({});
  const { isOpen: isDetailOpen, onOpen: openDetail, onClose: closeDetail } = useDisclosure();
  const [selectedLead, setSelectedLead] = useState(null);

  const url = process.env.REACT_APP_URL || "https://filmlocation.100billion.in";

  const getDisplayValue = (lead, key) => {
    if (key === "store_name") {
      return lead.store?.businessDetails?.businessName || lead.store_name || "—";
    }
    if (key === "category") {
      return lead.store?.category || lead.category || "—";
    }
    if (key === "createdAt") {
      return lead.createdAt
        ? new Date(lead.createdAt).toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        : "—";
    }
    if (key === "type") {
      return lead.type || "call";
    }
    return lead[key] ?? "—";
  };

  const fetchLead = async () => {
    try {
      const response = await fetch(`${url}/leads/leadsnew/${login.permanent_ID}`);
      const res = await response.json();
      const parsed = (res.user || []).map((lead) => ({
        ...lead,
        store: lead.store ? (typeof lead.store === "string" ? JSON.parse(lead.store) : lead.store) : null,
      }));
      setLeads(parsed);
      setFilteredLeads(parsed);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchLead();
  }, []);

  const planData = async () => {
    try {
      const response = await fetch(`${url}/store/getstorelist/mystore/${login.permanent_ID}`);
      const res = await response.json();
      setPlan(res);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    planData();
  }, []);

  const applyFilters = () => {
    let result = [...leads];
    if (search.trim() !== "") {
      result = result.filter(
        (lead) =>
          lead.name?.toLowerCase().includes(search.toLowerCase()) ||
          lead.phone?.includes(search) ||
          lead.email?.toLowerCase().includes(search.toLowerCase()) ||
          lead.store?.businessDetails?.businessName?.toLowerCase().includes(search.toLowerCase()) ||
          lead.store_name?.toLowerCase().includes(search.toLowerCase())
      );
    }
    const now = new Date();
    const applyLastDays = (days) =>
      result.filter((lead) => {
        const diff = (now - new Date(lead.createdAt)) / (1000 * 3600 * 24);
        return diff <= days;
      });
    switch (filterType) {
      case "today":
        result = result.filter((l) => new Date(l.createdAt).toDateString() === now.toDateString());
        break;
      case "yesterday":
        let y = new Date();
        y.setDate(y.getDate() - 1);
        result = result.filter((l) => new Date(l.createdAt).toDateString() === y.toDateString());
        break;
      case "7":
      case "14":
      case "28":
      case "60":
      case "90":
        result = applyLastDays(parseInt(filterType));
        break;
      case "custom":
        if (customFrom && customTo) {
          const from = new Date(customFrom);
          const to = new Date(customTo);
          result = result.filter((l) => {
            const d = new Date(l.createdAt);
            return d >= from && d <= to;
          });
        }
        break;
      default:
        break;
    }
    setFilteredLeads(result);
  };

  useEffect(() => {
    applyFilters();
  }, [search, filterType, customFrom, customTo, leads]);

  const updateStatusInBackend = async (id, newStatus) => {
    try {
      const response = await fetch(`${url}/leads/update-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, status: newStatus }),
      });
      const res = await response.json();
      if (response.ok) {
        toast({ description: "Status updated", status: "success", duration: 5000, position: "top-center", isClosable: true });
        setLeads((prev) => prev.map((lead) => (lead.id === id ? { ...lead, status: newStatus } : lead)));
      } else {
        toast({ description: res.message || "Failed", status: "error", duration: 5000, position: "top-center", isClosable: true });
      }
    } catch (error) {
      toast({ description: error.message, status: "error", duration: 5000, position: "top-center", isClosable: true });
    }
  };

  const saveConnectedTime = async (leadId, type) => {
    try {
      await fetch(`${url}/leads/leadsnew/update-connected-time`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: leadId, type }),
      });
      setLeads((prev) =>
        prev.map((lead) => (lead.id === leadId ? { ...lead, connected_time: new Date().toISOString() } : lead))
      );
    } catch (err) {
      console.log(err);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLeads.slice(indexOfFirstItem, indexOfLastItem);

  const isFree = !plan || !plan.sale_price || plan.sale_price === 0;

  return (
    <>
      <Header />
      <Box minH="100vh" bg="gray.50" py={8} px={{ base: 4, md: 8 }}>
        <Box maxW="1400px" mx="auto">
          <Heading size="lg" mb={6} color="gray.800" fontWeight="700">
            Leads
          </Heading>

          <Box bg="white" borderRadius="xl" boxShadow="0 4px 24px rgba(0,0,0,0.06)" overflow="hidden" mb={6}>
            <Flex p={4} gap={4} wrap="wrap" align="center" borderBottom="1px solid" borderColor="gray.100">
              <Input
                placeholder="Search by name, phone, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxW="320px"
                size="md"
                borderRadius="lg"
                borderColor="gray.200"
                _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
              />
              <Select
                maxW="180px"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                borderRadius="lg"
                borderColor="gray.200"
              >
                <option value="all">All</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="7">Last 7 Days</option>
                <option value="14">Last 14 Days</option>
                <option value="28">Last 28 Days</option>
                <option value="60">Last 60 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="custom">Custom</option>
              </Select>
              {filterType === "custom" && (
                <HStack>
                  <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} size="sm" maxW="150px" />
                  <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} size="sm" maxW="150px" />
                </HStack>
              )}
            </Flex>

            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="brand.600">
                  <Tr>
                    {LEAD_COLUMNS.map((col) => (
                      <Th key={col.key} color="white" fontWeight="600" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                        {col.label}
                      </Th>
                    ))}
                    <Th color="white" fontWeight="600" fontSize="xs" textTransform="uppercase">
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentItems.length === 0 ? (
                    <Tr>
                      <Td colSpan={LEAD_COLUMNS.length + 1} py={12} textAlign="center" color="gray.500">
                        No leads found
                      </Td>
                    </Tr>
                  ) : (
                    currentItems.map((lead) => (
                      <Tr
                        key={lead.id}
                        _hover={{ bg: "gray.50" }}
                        cursor="pointer"
                        onClick={() => {
                          setSelectedLead(lead);
                          openDetail();
                        }}
                      >
                        {LEAD_COLUMNS.map((col) => (
                          <Td key={col.key} fontSize="sm" py={3}>
                            {col.key === "status" ? (
                              <Select
                                value={lead.status || ""}
                                size="sm"
                                maxW="130px"
                                isDisabled={isFree}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => updateStatusInBackend(lead.id, e.target.value)}
                              >
                                <option value="Pending">Pending</option>
                                <option value="Cancelled">Cancelled</option>
                                <option value="Converted">Converted</option>
                                <option value="Not Interested">Not Interested</option>
                                <option value="In Progress">In Progress</option>
                              </Select>
                            ) : col.key === "type" ? (
                              <Badge colorScheme={lead.type === "booking" ? "purple" : "blue"} fontSize="xs">
                                {getDisplayValue(lead, col.key)}
                              </Badge>
                            ) : (
                              <Text noOfLines={1} maxW={["location_address", "reporting_datetime"].includes(col.key) ? "180px" : "140px"} title={getDisplayValue(lead, col.key)}>
                                {getDisplayValue(lead, col.key)}
                              </Text>
                            )}
                          </Td>
                        ))}
                        <Td py={2} onClick={(e) => e.stopPropagation()}>
                          <HStack spacing={1}>
                            <IconButton
                              aria-label="Call"
                              size="sm"
                              colorScheme="gray"
                              variant="outline"
                              icon={<PhoneIcon />}
                              isDisabled={isFree}
                              onClick={() => {
                                saveConnectedTime(lead.id, "call");
                                window.location.href = `tel:${lead.phone}`;
                              }}
                            />
                            <IconButton
                              aria-label="Email"
                              size="sm"
                              colorScheme="gray"
                              variant="outline"
                              icon={<EmailIcon />}
                              isDisabled={isFree}
                              onClick={() => {
                                saveConnectedTime(lead.id, "email");
                                window.location.href = `mailto:${lead.email}`;
                              }}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>

          <Flex justify="space-between" align="center" mt={4}>
            <Text fontSize="sm" color="gray.600">
              Page {currentPage} of {Math.max(1, Math.ceil(filteredLeads.length / itemsPerPage))}
            </Text>
            <HStack>
              <Button
                size="sm"
                variant="outline"
                isDisabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                isDisabled={currentPage >= Math.ceil(filteredLeads.length / itemsPerPage)}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </Button>
            </HStack>
          </Flex>
        </Box>
      </Box>

      <Modal isOpen={isDetailOpen} onClose={closeDetail} size="md" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader bg="brand.600" color="white" borderTopRadius="xl">
            Lead Details
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={6}>
            {selectedLead && (
              <VStack align="stretch" spacing={3}>
                {LEAD_COLUMNS.map((col) => (
                  <Flex key={col.key} justify="space-between" align="start">
                    <Text fontWeight="600" color="gray.600" fontSize="sm">
                      {col.label}:
                    </Text>
                    <Text fontSize="sm" textAlign="right" maxW="60%" wordBreak="break-word">
                      {getDisplayValue(selectedLead, col.key)}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default Leads;
